import LocalShippingRoundedIcon from '@mui/icons-material/LocalShippingRounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import { Stack } from '@mui/material';

import {
  createDeliveryProvider,
  deleteDeliveryProvider,
  listDeliveryProviders,
  testDeliveryProviderConnection,
  updateDeliveryProvider,
} from '../../api/deliverySettingsApi';
import {
  settingsKeys,
  useDeliverySettings,
  useSettingsQuery,
  useUpdateDeliverySettings,
} from '../../hooks';
import { toSettingsUpdate, useDomainPermissions, useFeedback, useSettingsForm } from '../../lib';
import type { DeliveryModel, ProviderSummary } from '../../model/settingsTypes';
import {
  ChipListField,
  FeedbackSnackbar,
  MultiToggleField,
  ProviderManager,
  SelectField,
  SettingsCard,
  SettingsScaffold,
  SwitchField,
} from '../components';
import { CURRENCY_OPTIONS, DELIVERY_MODEL_OPTIONS } from '../options';

/** Delivery models, providers, credentials and fallbacks. */
export function DeliverySettingsPage() {
  const { canView, canManage, canManageCredentials } = useDomainPermissions('delivery');
  const query = useDeliverySettings();
  const update = useUpdateDeliverySettings();
  const form = useSettingsForm(query.data);
  const { feedback, notify, success, error: notifyError, clear } = useFeedback();

  // Shared cache entry with the ProviderManager — used to populate the fallback select.
  const providersQuery = useSettingsQuery<ProviderSummary[]>(
    settingsKeys.deliveryProviders,
    listDeliveryProviders,
  );
  const fallbackOptions = (providersQuery.data ?? []).map((provider) => ({
    value: provider.id,
    label: `${provider.name} (${provider.code})`,
  }));

  const handleSave = () => {
    if (!form.draft) {
      return;
    }
    update.mutate(toSettingsUpdate(form.draft), {
      onSuccess: () => success('Delivery settings saved.'),
      onError: (error) => {
        notifyError(error.message);
        if (error.status === 409) {
          void query.refetch();
        }
      },
    });
  };

  return (
    <SettingsScaffold
      title="Delivery settings"
      subtitle="Delivery models, providers, credentials and fallbacks."
      icon={<LocalShippingRoundedIcon />}
      query={query}
      canView={canView}
      canManage={canManage}
      audit={query.data}
      dirty={form.dirty}
      saving={update.isPending}
      onSave={handleSave}
      onReset={form.reset}
      feedback={<FeedbackSnackbar feedback={feedback} onClose={clear} />}
    >
      {() => {
        const draft = form.draft;
        if (!draft) {
          return null;
        }
        const disabled = !canManage;
        return (
          <Stack spacing={2.5}>
            <SettingsCard title="Delivery models" icon={<LocalShippingRoundedIcon />}>
              <MultiToggleField
                label="Enabled models"
                description="Fulfilment options offered to customers."
                value={draft.enabledModels}
                options={DELIVERY_MODEL_OPTIONS}
                onChange={(value) => form.setField('enabledModels', value as DeliveryModel[])}
                disabled={disabled}
              />
              <SwitchField
                label="Courier delivery"
                checked={draft.courierEnabled}
                onChange={(value) => form.setField('courierEnabled', value)}
                disabled={disabled}
              />
              <SwitchField
                label="Pickup"
                checked={draft.pickupEnabled}
                onChange={(value) => form.setField('pickupEnabled', value)}
                disabled={disabled}
              />
              <SwitchField
                label="Same-day delivery"
                checked={draft.sameDayEnabled}
                onChange={(value) => form.setField('sameDayEnabled', value)}
                disabled={disabled}
              />
            </SettingsCard>

            <SettingsCard title="Rules & fallbacks" icon={<TuneRoundedIcon />}>
              <SelectField
                label="Default currency"
                value={draft.defaultCurrency}
                options={CURRENCY_OPTIONS}
                onChange={(value) => form.setField('defaultCurrency', value)}
                disabled={disabled}
              />
              <SelectField
                label="Fallback provider"
                description="Used when no other provider can fulfil an order."
                value={draft.fallbackProviderId ?? ''}
                options={fallbackOptions}
                onChange={(value) => form.setField('fallbackProviderId', value || null)}
                disabled={disabled}
                allowEmpty
                emptyLabel="None"
              />
              <ChipListField
                label="Restrictions"
                description="Delivery restrictions (regions, weight, etc.)."
                value={draft.restrictions}
                onChange={(value) => form.setField('restrictions', value)}
                disabled={disabled}
              />
            </SettingsCard>

            <ProviderManager
              title="Delivery providers"
              description="Carriers and their credentials."
              icon={<LocalShippingRoundedIcon />}
              listKey={settingsKeys.deliveryProviders}
              api={{
                list: listDeliveryProviders,
                create: createDeliveryProvider,
                update: updateDeliveryProvider,
                remove: deleteDeliveryProvider,
                test: testDeliveryProviderConnection,
              }}
              canManage={canManage}
              canManageCredentials={canManageCredentials}
              credentialHint="Carrier API key"
              onFeedback={notify}
            />
          </Stack>
        );
      }}
    </SettingsScaffold>
  );
}
